/**
 * AnalyticsPage — the FLAGSHIP cross-lab analytics section (the ERP-grade reporting centre of the platform
 * super-admin console). A rich multi-sub-section surface: a WAI-ARIA <Tabs> switches between eight
 * professional Recharts panels, all windowed by ONE shared period picker (30 j / 90 j / 12 mois):
 *
 *   • Revenue        — MRR/ARR run-rate KPIs + settled-cash area trend + run-rate-by-tier donut + a
 *                      per-plan ranked table.
 *   • Subscriptions  — total/active/new KPIs + status donut + a per-plan × per-statut ranked table.
 *   • Wilayas        — active-lab geographic distribution (ranked bars + a top-cohorts table).
 *   • Referrals      — lab↔doctor connection: links-by-statut, free-vs-paid donut, referral-% KPIs,
 *                      demand-band donut + a top-demand-labs table.
 *   • Support        — helpdesk burden by category (ranked bars) + an open-vs-total table + a
 *                      per-category opened-tickets trend.
 *   • Modules        — most-requested product modules (ranked bars) + a route-pattern ranked table.
 *   • Analyzers      — most-used analyzers (ranked bars + a results/errors table).
 *   • Analysis-types — most-in-demand analysis parameters (ranked bars + a table, source-badge aware).
 *
 * Gating: super-admin-only (route rides <PlatformAdminGuard>; this screen ALSO renders its own
 * <ForbiddenState> defensively off `useIsPlatformAdmin()` — est_admin_plateforme ONLY, no management.*
 * fallback). Full 5-state DoD per active panel (Loading / Empty / Error / Offline / Forbidden) + the
 * cloud-only "not available on this install" short-circuit. Token-driven, AA, FR i18n.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
  Tabs,
} from "@/ui";
import type { TabItem } from "@/ui";
import { useT, useUiStore } from "@/app";
import { token } from "@/design/theme";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { isManagementAbsent } from "../api";
import { useIsPlatformAdmin } from "../session";
import type { AnalyticsPeriod } from "../analytics.types";
import {
  useAnalysisTypesAnalytics,
  useAnalyzersAnalytics,
  useModulesAnalytics,
  useReferralsAnalytics,
  useRevenueAnalytics,
  useSubscriptionsAnalytics,
  useSupportAnalytics,
  useWilayasAnalytics,
} from "../analytics.api";
import {
  DEMAND_BAND_COLOR,
  FREE_PAID_COLOR,
  bucketForPeriod,
  fmtCount,
  fmtDzdCompact,
  fmtDzdCentimes,
  fmtPct1,
  mergeTrendSeries,
  paletteColor,
  sum,
  toColoredDonut,
  toRankedBars,
} from "../analytics.lib";
import { SUB_STATUT_COLOR } from "../metrics.lib";
import { PageHeader } from "../components/PageHeader";
import { Screen, TileGrid } from "../components/Screen";
import { KpiTile } from "../components/KpiTile";
import { MetricsPeriodSwitch } from "../components/MetricsPeriodSwitch";
import {
  AnalyticsDonut,
  RankedBarChart,
  RankedTable,
  TrendChart,
  type RankedRow,
} from "../components/AnalyticsCharts";

/** The default top-N the ranking panels request (validator clamps 1..100 server-side). */
const TOP_N = 20;

/** A two-up responsive grid for the panel cards (collapses to one column on narrow viewports). */
function PanelGrid({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "grid", gap: "var(--nv-s-4)", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))" }}>
      {children}
    </div>
  );
}

/** A small slate caption used as a Card action (the period / source context). */
function Caption({ text }: { text: string }) {
  return <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>{text}</span>;
}

/**
 * Shared per-panel status frame. Each sub-section is an independent query, so it honours its OWN 5-state
 * slice: an errored/absent panel degrades in place without taking the tablist down. Renders `children`
 * only on the happy path (the caller has data).
 */
function PanelState({
  query,
  refresh,
  hasData,
  children,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; isFetching: boolean };
  refresh: () => void;
  hasData: boolean;
  children: ReactNode;
}) {
  const { t } = useT();
  if (query.isLoading) return <LoadingState variant="table" rows={4} />;
  if (query.isError) {
    if (isManagementAbsent(query.error)) {
      return (
        <Card title={t("mgmt.unavailable.title")}>
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.unavailable.desc")}</p>
        </Card>
      );
    }
    const err = query.error as { message?: string; code?: string };
    return (
      <ErrorState
        message={err?.message ?? t("mgmt.analytics.error")}
        {...(err?.code ? { code: err.code } : {})}
        onRetry={refresh}
      />
    );
  }
  if (!hasData) {
    return <EmptyState title={t("mgmt.analytics.chart.empty.title")} description={t("mgmt.analytics.chart.empty.desc")} />;
  }
  return <>{children}</>;
}

// ══ REVENUE PANEL ═══════════════════════════════════════════════════════════════════════

function RevenuePanel({ period, enabled }: { period: AnalyticsPeriod; enabled: boolean }) {
  const { t } = useT();
  const q = useRevenueAnalytics(period, enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const tierDonut = d
    ? toColoredDonut(
        Object.entries(d.recurring.byTier).map(([tier, centimes], i) => ({
          key: tier,
          label: tier,
          value: centimes,
          color: paletteColor(i),
        })),
      )
    : [];

  const settledSeries = d ? d.settled.series.map((p) => ({ date: p.date, cash: p.value_centimes })) : [];

  const planRows: RankedRow[] = d
    ? d.recurring.byPlan.map((p) => ({
        key: p.plan ?? "(sans plan)",
        label: p.nom ?? p.plan ?? t("mgmt.analytics.revenue.unpriced"),
        sub: p.priced
          ? `${p.tier ?? ""} · ${fmtDzdCentimes(p.monthly_centimes)}/mois`
          : t("mgmt.analytics.revenue.unpricedHint"),
        value: p.monthly_centimes * p.count,
        secondary: { label: t("mgmt.analytics.revenue.subs"), value: p.count },
      }))
    : [];

  const hasData = !!d;

  return (
    <PanelState query={q} refresh={refresh} hasData={hasData}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile
              label={t("mgmt.analytics.revenue.mrr")}
              value={fmtDzdCompact(d.recurring.mrr_centimes)}
              hint={t("mgmt.analytics.revenue.mrr.hint")}
              tone="accent"
              icon="◆"
            />
            <KpiTile
              label={t("mgmt.analytics.revenue.arr")}
              value={fmtDzdCompact(d.recurring.arr_centimes)}
              hint={t("mgmt.analytics.revenue.arr.hint")}
              tone="success"
              icon="↑"
            />
            <KpiTile
              label={t("mgmt.analytics.revenue.settled")}
              value={fmtDzdCompact(d.settled.total_centimes)}
              hint={t("mgmt.analytics.revenue.settled.hint", { n: d.settled.paymentCount })}
              tone="neutral"
              icon="✓"
            />
            <KpiTile
              label={t("mgmt.analytics.revenue.priced")}
              value={fmtCount(d.recurring.pricedSubscriptions)}
              hint={t("mgmt.analytics.revenue.priced.hint", { n: d.recurring.unpricedSubscriptions })}
              tone={d.recurring.unpricedSubscriptions > 0 ? "warning" : "neutral"}
              icon="≣"
            />
          </TileGrid>

          <PanelGrid>
            <Card title={t("mgmt.analytics.revenue.trend.title")} actions={<Caption text={t(`mgmt.overview.period.${period}`)} />}>
              <TrendChart
                data={settledSeries}
                bucket={bucketForPeriod(period)}
                series={[{ dataKey: "cash", label: t("mgmt.analytics.revenue.settled"), color: token.success }]}
                ariaLabel={t("mgmt.analytics.revenue.trend.aria")}
                area
                valueFmt={fmtDzdCompact}
              />
            </Card>

            <Card title={t("mgmt.analytics.revenue.byTier.title")}>
              <AnalyticsDonut data={tierDonut} ariaLabel={t("mgmt.analytics.revenue.byTier.aria")} valueFmt={fmtDzdCompact} />
            </Card>
          </PanelGrid>

          <Card title={t("mgmt.analytics.revenue.byPlan.title")} padded={false}>
            <RankedTable rows={planRows} metricHeader={t("mgmt.analytics.revenue.byPlan.metric")} valueFmt={fmtDzdCompact} />
          </Card>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ SUBSCRIPTIONS PANEL ═════════════════════════════════════════════════════════════════

function SubscriptionsPanel({ period, enabled }: { period: AnalyticsPeriod; enabled: boolean }) {
  const { t } = useT();
  const q = useSubscriptionsAnalytics(period, enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const statutDonut = d
    ? toColoredDonut(
        Object.entries(d.byStatut).map(([statut, count]) => ({
          key: statut,
          label: t(`mgmt.overview.subStatut.${statut}`),
          value: count,
          color: SUB_STATUT_COLOR[statut] ?? token.steel,
        })),
      )
    : [];

  const planRows: RankedRow[] = d
    ? d.byPlan.map((p) => ({
        key: p.plan ?? "(sans plan)",
        label: p.plan ?? t("mgmt.analytics.subs.noPlan"),
        sub: t("mgmt.analytics.subs.planSub", { total: p.total }),
        value: p.active,
        secondary: { label: t("mgmt.analytics.subs.total"), value: p.total },
      }))
    : [];

  return (
    <PanelState query={q} refresh={refresh} hasData={!!d}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile label={t("mgmt.analytics.subs.active")} value={fmtCount(d.active)} tone="success" icon="◆" />
            <KpiTile label={t("mgmt.analytics.subs.totalKpi")} value={fmtCount(d.total)} tone="accent" icon="≣" />
            <KpiTile
              label={t("mgmt.analytics.subs.new")}
              value={fmtCount(d.newInWindow)}
              hint={t(`mgmt.overview.period.${period}`)}
              tone="neutral"
              icon="↑"
            />
          </TileGrid>

          <PanelGrid>
            <Card title={t("mgmt.analytics.subs.donut.title")}>
              <AnalyticsDonut data={statutDonut} ariaLabel={t("mgmt.analytics.subs.donut.aria")} />
            </Card>
            <Card title={t("mgmt.analytics.subs.byPlan.title")} padded={false}>
              <RankedTable rows={planRows} metricHeader={t("mgmt.analytics.subs.active")} />
            </Card>
          </PanelGrid>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ WILAYAS PANEL ═══════════════════════════════════════════════════════════════════════

function WilayasPanel({ enabled }: { enabled: boolean }) {
  const { t } = useT();
  const q = useWilayasAnalytics(enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const bars = d
    ? toRankedBars(d.cohorts.slice(0, TOP_N).map((c) => ({ key: c.label, label: c.label, value: c.count })))
    : [];
  const rows: RankedRow[] = d
    ? d.cohorts.map((c) => ({ key: c.label, label: c.label, value: c.count }))
    : [];

  return (
    <PanelState query={q} refresh={refresh} hasData={!!d}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile label={t("mgmt.analytics.wilayas.total")} value={fmtCount(d.total)} tone="accent" icon="⬡" />
            <KpiTile label={t("mgmt.analytics.wilayas.distinct")} value={fmtCount(d.distinctWilayas)} tone="neutral" icon="◈" />
          </TileGrid>
          <PanelGrid>
            <Card title={t("mgmt.analytics.wilayas.bars.title")}>
              <RankedBarChart data={bars} ariaLabel={t("mgmt.analytics.wilayas.bars.aria")} />
            </Card>
            <Card title={t("mgmt.analytics.wilayas.table.title")} padded={false}>
              <RankedTable rows={rows} metricHeader={t("mgmt.analytics.wilayas.metric")} />
            </Card>
          </PanelGrid>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ REFERRALS PANEL ═════════════════════════════════════════════════════════════════════

function ReferralsPanel({ period, enabled }: { period: AnalyticsPeriod; enabled: boolean }) {
  const { t } = useT();
  const q = useReferralsAnalytics(period, enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const freePaidDonut = d
    ? toColoredDonut([
        { key: "free", label: t("mgmt.analytics.ref.free"), value: d.freeVsPaid.free, color: FREE_PAID_COLOR.free },
        { key: "paid", label: t("mgmt.analytics.ref.paid"), value: d.freeVsPaid.paid, color: FREE_PAID_COLOR.paid },
      ])
    : [];

  const bandDonut = d
    ? toColoredDonut(
        (["none", "low", "medium", "high"] as const).map((b) => ({
          key: b,
          label: t(`mgmt.analytics.ref.band.${b}`),
          value: d.demandLevels.bands[b],
          color: DEMAND_BAND_COLOR[b]!,
        })),
      )
    : [];

  const topLabRows: RankedRow[] = d
    ? d.demandLevels.topLabs.map((l, i) => ({
        key: l.laboratoire_id,
        label: t("mgmt.analytics.ref.lab", { n: i + 1 }),
        sub: `#${l.laboratoire_id}`,
        value: l.returns,
      }))
    : [];

  const pct = d?.freeVsPaid.referralPct;

  return (
    <PanelState query={q} refresh={refresh} hasData={!!d}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile label={t("mgmt.analytics.ref.links")} value={fmtCount(d.links.total)} tone="accent" icon="⇄" />
            <KpiTile
              label={t("mgmt.analytics.ref.approved")}
              value={fmtCount(d.links.byStatut.approuve)}
              hint={t("mgmt.analytics.ref.approved.hint", { i: d.links.byStatut.invite })}
              tone="success"
              icon="✓"
            />
            <KpiTile
              label={t("mgmt.analytics.ref.pct")}
              value={pct && pct.avg != null ? fmtPct1(pct.avg) : "—"}
              hint={
                pct && pct.avg != null
                  ? t("mgmt.analytics.ref.pct.hint", { min: fmtPct1(pct.min ?? 0), max: fmtPct1(pct.max ?? 0), n: pct.sampled })
                  : t("mgmt.analytics.ref.pct.none")
              }
              tone="neutral"
              icon="%"
            />
            <KpiTile
              label={t("mgmt.analytics.ref.newReturns")}
              value={fmtCount(d.demandLevels.newReturnsInWindow)}
              hint={t(`mgmt.overview.period.${period}`)}
              tone="neutral"
              icon="↑"
            />
          </TileGrid>

          <PanelGrid>
            <Card title={t("mgmt.analytics.ref.freePaid.title")}>
              <AnalyticsDonut data={freePaidDonut} ariaLabel={t("mgmt.analytics.ref.freePaid.aria")} />
            </Card>
            <Card title={t("mgmt.analytics.ref.bands.title")}>
              <AnalyticsDonut data={bandDonut} ariaLabel={t("mgmt.analytics.ref.bands.aria")} />
            </Card>
          </PanelGrid>

          <Card title={t("mgmt.analytics.ref.topLabs.title")} padded={false}>
            <RankedTable rows={topLabRows} metricHeader={t("mgmt.analytics.ref.returns")} />
          </Card>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ SUPPORT PANEL ═══════════════════════════════════════════════════════════════════════

function SupportPanel({ period, enabled }: { period: AnalyticsPeriod; enabled: boolean }) {
  const { t } = useT();
  const q = useSupportAnalytics(period, enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const bars = d
    ? toRankedBars(d.byCategorie.map((c) => ({ key: c.categorie, label: t(`mgmt.overview.cat.${c.categorie}`), value: c.total })))
    : [];

  const rows: RankedRow[] = d
    ? d.byCategorie.map((c) => ({
        key: c.categorie,
        label: t(`mgmt.overview.cat.${c.categorie}`),
        value: c.total,
        secondary: { label: t("mgmt.analytics.support.open"), value: c.open, tone: c.open > 0 ? "danger" : "neutral" },
      }))
    : [];

  const trendData = d ? mergeTrendSeries(d.trend.buckets, d.trend.series) : [];
  const trendSeries = d
    ? d.trend.series.map((s, i) => ({ dataKey: s.categorie, label: t(`mgmt.overview.cat.${s.categorie}`), color: paletteColor(i) }))
    : [];

  return (
    <PanelState query={q} refresh={refresh} hasData={!!d}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile label={t("mgmt.analytics.support.total")} value={fmtCount(d.total)} tone="accent" icon="✉" />
            <KpiTile
              label={t("mgmt.analytics.support.openTotal")}
              value={fmtCount(sum(d.byCategorie.map((c) => c.open)))}
              tone={sum(d.byCategorie.map((c) => c.open)) > 0 ? "warning" : "neutral"}
              icon="●"
            />
          </TileGrid>
          <PanelGrid>
            <Card title={t("mgmt.analytics.support.bars.title")}>
              <RankedBarChart data={bars} ariaLabel={t("mgmt.analytics.support.bars.aria")} />
            </Card>
            <Card title={t("mgmt.analytics.support.table.title")} padded={false}>
              <RankedTable rows={rows} metricHeader={t("mgmt.analytics.support.total")} />
            </Card>
          </PanelGrid>
          <Card title={t("mgmt.analytics.support.trend.title")} actions={<Caption text={t(`mgmt.overview.period.${period}`)} />}>
            <TrendChart
              data={trendData}
              bucket={bucketForPeriod(period)}
              series={trendSeries}
              ariaLabel={t("mgmt.analytics.support.trend.aria")}
            />
          </Card>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ MODULES PANEL ═══════════════════════════════════════════════════════════════════════

function ModulesPanel({ period, enabled }: { period: AnalyticsPeriod; enabled: boolean }) {
  const { t } = useT();
  const q = useModulesAnalytics(period, TOP_N, enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const bars = d ? toRankedBars(d.byModule.map((m) => ({ key: m.module, label: m.module, value: m.count }))) : [];
  const routeRows: RankedRow[] = d
    ? d.byRoute.map((r) => ({ key: `${r.module}:${r.route}`, label: r.route, sub: r.module, value: r.count }))
    : [];

  return (
    <PanelState query={q} refresh={refresh} hasData={!!d}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile
              label={t("mgmt.analytics.modules.total")}
              value={fmtCount(d.total)}
              hint={t(`mgmt.overview.period.${period}`)}
              tone="accent"
              icon="▤"
            />
            <KpiTile label={t("mgmt.analytics.modules.distinct")} value={fmtCount(d.byModule.length)} tone="neutral" icon="≣" />
          </TileGrid>
          <PanelGrid>
            <Card title={t("mgmt.analytics.modules.bars.title")}>
              <RankedBarChart data={bars} ariaLabel={t("mgmt.analytics.modules.bars.aria")} />
            </Card>
            <Card title={t("mgmt.analytics.modules.routes.title")} padded={false}>
              <RankedTable rows={routeRows} metricHeader={t("mgmt.analytics.modules.requests")} />
            </Card>
          </PanelGrid>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ ANALYZERS PANEL ═════════════════════════════════════════════════════════════════════

function AnalyzersPanel({ period, enabled }: { period: AnalyticsPeriod; enabled: boolean }) {
  const { t } = useT();
  const q = useAnalyzersAnalytics(period, TOP_N, enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const bars = d
    ? toRankedBars(d.byAnalyzer.map((a) => ({ key: a.automate_id, label: a.libelle, value: a.results })))
    : [];
  const rows: RankedRow[] = d
    ? d.byAnalyzer.map((a) => ({
        key: a.automate_id,
        label: a.libelle,
        sub: [a.fabricant, a.modele, a.discipline_code].filter(Boolean).join(" · ") || undefined,
        value: a.results,
        secondary: { label: t("mgmt.analytics.analyzers.errors"), value: a.errors, tone: a.errors > 0 ? "danger" : "neutral" },
      }))
    : [];

  return (
    <PanelState query={q} refresh={refresh} hasData={!!d}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile
              label={t("mgmt.analytics.analyzers.total")}
              value={fmtCount(d.total)}
              hint={t(`mgmt.overview.period.${period}`)}
              tone="accent"
              icon="⚙"
            />
            <KpiTile label={t("mgmt.analytics.analyzers.count")} value={fmtCount(d.byAnalyzer.length)} tone="neutral" icon="≣" />
          </TileGrid>
          <PanelGrid>
            <Card title={t("mgmt.analytics.analyzers.bars.title")}>
              <RankedBarChart data={bars} ariaLabel={t("mgmt.analytics.analyzers.bars.aria")} />
            </Card>
            <Card title={t("mgmt.analytics.analyzers.table.title")} padded={false}>
              <RankedTable rows={rows} metricHeader={t("mgmt.analytics.analyzers.results")} />
            </Card>
          </PanelGrid>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ ANALYSIS-TYPES PANEL ════════════════════════════════════════════════════════════════

function AnalysisTypesPanel({ period, enabled }: { period: AnalyticsPeriod; enabled: boolean }) {
  const { t } = useT();
  const q = useAnalysisTypesAnalytics(period, TOP_N, enabled);
  const d = q.data;
  const refresh = () => void q.refetch();

  const bars = d
    ? toRankedBars(d.byParametre.map((p) => ({ key: p.code, label: p.libelle, value: p.count })))
    : [];
  const rows: RankedRow[] = d
    ? d.byParametre.map((p) => ({
        key: p.code,
        label: p.libelle,
        sub: [p.code, p.discipline_code, p.unite].filter(Boolean).join(" · ") || undefined,
        value: p.count,
      }))
    : [];

  const sourceLabel = d && d.source !== "empty" ? t(`mgmt.analytics.types.source.${d.source}`) : null;

  return (
    <PanelState query={q} refresh={refresh} hasData={!!d}>
      {d && (
        <Screen>
          <TileGrid min={220}>
            <KpiTile
              label={t("mgmt.analytics.types.total")}
              value={fmtCount(d.total)}
              hint={t(`mgmt.overview.period.${period}`)}
              tone="accent"
              icon="🧪"
            />
            <KpiTile label={t("mgmt.analytics.types.distinct")} value={fmtCount(d.byParametre.length)} tone="neutral" icon="≣" />
          </TileGrid>
          <PanelGrid>
            <Card
              title={t("mgmt.analytics.types.bars.title")}
              actions={sourceLabel ? <Badge tone="neutral">{sourceLabel}</Badge> : undefined}
            >
              <RankedBarChart data={bars} ariaLabel={t("mgmt.analytics.types.bars.aria")} />
            </Card>
            <Card title={t("mgmt.analytics.types.table.title")} padded={false}>
              <RankedTable rows={rows} metricHeader={t("mgmt.analytics.types.demand")} />
            </Card>
          </PanelGrid>
        </Screen>
      )}
    </PanelState>
  );
}

// ══ THE SCREEN ══════════════════════════════════════════════════════════════════════════

/** The tab ids — the single source the tablist + the panel switch read. */
const TAB_IDS = [
  "revenue",
  "subscriptions",
  "wilayas",
  "referrals",
  "support",
  "modules",
  "analyzers",
  "analysis-types",
] as const;
type TabId = (typeof TAB_IDS)[number];

export function AnalyticsPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  const [tab, setTab] = useState<TabId>("revenue");

  const enabled = isAdmin && online;

  const header = (
    <PageHeader
      title={t("mgmt.analytics.title")}
      subtitle={t("mgmt.analytics.subtitle")}
      actions={<MetricsPeriodSwitch value={period} onChange={setPeriod} />}
    />
  );

  // ── Screen-level gating (Forbidden / Offline) — the per-panel states handle the rest ──
  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => window.location.reload()} />
      </Screen>
    );
  }

  // Only the ACTIVE panel mounts (a tab switch mounts the next one → its query fires lazily).
  const panelFor = (id: TabId): ReactNode => {
    switch (id) {
      case "revenue":
        return <RevenuePanel period={period} enabled={enabled} />;
      case "subscriptions":
        return <SubscriptionsPanel period={period} enabled={enabled} />;
      case "wilayas":
        return <WilayasPanel enabled={enabled} />;
      case "referrals":
        return <ReferralsPanel period={period} enabled={enabled} />;
      case "support":
        return <SupportPanel period={period} enabled={enabled} />;
      case "modules":
        return <ModulesPanel period={period} enabled={enabled} />;
      case "analyzers":
        return <AnalyzersPanel period={period} enabled={enabled} />;
      case "analysis-types":
        return <AnalysisTypesPanel period={period} enabled={enabled} />;
      default:
        return null;
    }
  };

  const items: TabItem[] = TAB_IDS.map((id) => ({
    id,
    label: t(`mgmt.analytics.tab.${id}`),
    // Only the active tab's content is built (Tabs renders just the active panel).
    content: id === tab ? panelFor(id) : null,
  }));

  return (
    <Screen>
      {header}
      <Tabs items={items} value={tab} onChange={(id) => setTab(id as TabId)} aria-label={t("mgmt.analytics.tabs.aria")} />
    </Screen>
  );
}
